<script setup>
import { ref, computed } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { formatShortDate } from '@/lib/utils'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { useCurrency } from '@/composables/currency'
import { useDetectDevice } from '@/composables/device'
import { useProjectColumn } from '@/composables/data-grid-column'
import { formatDuration, formatTimeValue, isReportFieldModified, parseDuration } from '@/composables/report'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { infoBoxIdEnum } from '@/stores/portal.store'

import InfoBox from '@/components/shared/InfoBox.vue'
import InvoiceLineEdit from '@/components/accounting/InvoiceLineEdit.vue'

const props = defineProps({
  rows: {
    type: Array,
    required: true,
  },
  subject: {
    type: Number,
    required: true,
  },
})

const emit = defineEmits(['change', 'exclusion-change'])

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { getCurrencyString } = useCurrency()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const { reportRowKindEnum, reportTimeNoteEnum, invoiceLineFieldTypeEnum } = accountingStore

const tab = ref(reportRowKindEnum.TIME)
const showInternal = ref(false)
const editedLine = ref(null)

const isStaffReport = computed(() => accountingStore.isStaffSubject(props.subject))
const timeRows = computed(() => props.rows.filter(row => row.kind === reportRowKindEnum.TIME))
const expenseRows = computed(() => props.rows.filter(row => row.kind === reportRowKindEnum.EXPENSE))
const serviceRows = computed(() => props.rows.filter(row => row.kind === reportRowKindEnum.SERVICE))
const locationNames = computed(() => new Map(accountingStore.getLocalizedBudgetTags().map(tag => [tag.id, tag.name])))

if (!accountingStore.budgetTags.length) accountingStore.fetchBudgetTagsAsync({ all: true }, true)
if (!planningStore.projects.length) planningStore.fetchProjectsAsync({}, true)
const referenceCode = () => webSiteStore.getDefaultCurrency()?.code
const money = (value, code) => (value == null ? '' : getCurrencyString(+value, code || referenceCode()))


const changeRow = (key, changes) => emit('change', props.rows.map(row => (row.key === key ? { ...row, ...changes } : row)))
const setExcluded = (excludedByKey) => {
  if (!props.rows.some(row => excludedByKey.has(row.key) && excludedByKey.get(row.key) !== !!row.is_excluded)) return
  emit('exclusion-change', props.rows.map(row => (excludedByKey.has(row.key) ? { ...row, is_excluded: excludedByKey.get(row.key) } : row)))
}
const onSelectionChanged = ({ currentSelectedRowKeys, currentDeselectedRowKeys }) => setExcluded(new Map([
  ...currentSelectedRowKeys.map(key => [key, false]),
  ...currentDeselectedRowKeys.map(key => [key, true]),
]))
const includedKeys = (rows) => rows.filter(row => !row.is_excluded).map(row => row.key)

const column = (dataField, caption, options = {}) => ({ dataField, name: dataField, caption: i18n.t(caption), allowEditing: false, ...options })
const durationColumn = (dataField, caption, editable = true) => column(dataField, caption, {
  dataType: 'string',
  alignment: 'right',
  width: '6.5rem',
  allowEditing: editable,
  calculateDisplayValue: (row) => formatDuration(row[dataField]),
  calculateCellValue: (row) => formatDuration(row[dataField]),
  setCellValue: (newData, value) => { newData[dataField] = parseDuration(value) },
})
const timeColumn = (dataField, caption) => column(dataField, caption, {
  dataType: 'string',
  width: '5.5rem',
  allowEditing: true,
  calculateCellValue: (row) => formatTimeValue(row[dataField]),
  calculateDisplayValue: (row) => {
    const text = formatTimeValue(row[dataField])
    const suffix = dataField === 'end' && row.crosses_midnight ? ' (+1)' : ''
    return row.time_note === reportTimeNoteEnum.ASSUMED_START && text ? `${text}*${suffix}` : `${text}${suffix}`
  },
  setCellValue: (newData, value) => { newData[dataField] = value || null },
})
const moneyColumn = (dataField, caption, options = {}) => column(dataField, caption, {
  dataType: 'number',
  width: '8rem',
  calculateDisplayValue: (row) => money(row[dataField], options.reference ? null : row.currency_code),
  ...options,
})

const leadingColumns = () => [
  column('date', 'print.report.columns.date', { dataType: 'date', width: '7rem', format: 'dd/MM/yyyy' }),
  useProjectColumn({
    dataField: 'project_id',
    caption: i18n.t('print.report.columns.project'),
    width: '12rem',
    allowEditing: false,
    fallbackText: row => row.project_name,
    calculateSortValue: row => row.project_name ?? '',
  }),
]
const personColumns = () => [...leadingColumns(), ...(isStaffReport.value ? [] : [column('staff_label', 'print.report.columns.staff', { width: '10rem' })])]

const timeColumns = computed(() => [
  ...personColumns(),
  timeColumn('start', 'print.report.columns.start'),
  timeColumn('end', 'print.report.columns.end'),
  durationColumn('pause_minutes', 'print.report.columns.pause'),
  durationColumn('gross_minutes', 'print.report.columns.gross'),
  durationColumn('net_minutes', 'print.report.columns.net'),
  durationColumn('travel_minutes', 'print.report.columns.travel'),
  durationColumn('night_minutes', 'print.report.columns.night'),
  column('comments', 'print.report.columns.comments', { allowEditing: true, width: '16rem' }),
  column('time_note', 'print.report.columns.time_note', {
    width: '12rem',
    calculateDisplayValue: (row) => (row.time_note === reportTimeNoteEnum.ASSUMED_START ? i18n.t('print.report.timeNoteAssumed') : ''),
  }),
  ...(showInternal.value ? [
    durationColumn('internal_minutes', 'print.report.columns.internal', false),
    durationColumn('client_minutes', 'print.report.columns.client_hours'),
    moneyColumn('hourly_cost', 'print.report.columns.hourly_cost', { allowEditing: true, reference: true }),
    moneyColumn('internal_cost', 'print.report.columns.internal_cost', { reference: true }),
    moneyColumn('client_value', 'print.report.columns.client_value', { allowEditing: true }),
    moneyColumn('client_value_reference', 'print.report.columns.client_value_reference', { reference: true }),
    column('invoice_numbers', 'print.report.columns.invoice', { width: '8rem' }),
  ] : []),
])

const expenseColumns = computed(() => [
  ...personColumns(),
  column('notes', 'print.report.columns.notes', { allowEditing: true, width: '16rem' }),
  column('quantity', 'print.report.columns.quantity', { dataType: 'number', width: '5rem', allowEditing: true }),
  moneyColumn('amount', 'print.report.columns.amount', { allowEditing: true }),
  moneyColumn('amount_reference', 'print.report.columns.amount_reference', { reference: true }),
  ...(showInternal.value ? [
    moneyColumn('client_value', 'print.report.columns.client_value', { allowEditing: true }),
    column('invoice_numbers', 'print.report.columns.invoice', { width: '8rem' }),
  ] : []),
])

const serviceColumns = computed(() => [
  ...leadingColumns(),
  column('comments', 'common.report.serviceItem', { width: '14rem' }),
  column('quantity', 'print.report.columns.quantity', { dataType: 'number', width: '5.5rem', allowEditing: true }),
  timeColumn('start', 'print.report.columns.start'),
  timeColumn('end', 'print.report.columns.end'),
  column('staff_count', 'common.report.people', { dataType: 'number', width: '5.5rem', allowEditing: true }),
  column('client_reference', 'common.requestReference', { width: '9rem', allowEditing: true }),
  column('location_id', 'common.location', { width: '10rem', calculateDisplayValue: (row) => locationNames.value.get(row.location_id) || '' }),
  moneyColumn('amount', 'print.report.columns.amount'),
])

const summaryItems = (fields) => fields.map(field => ({
  name: field,
  showInColumn: field,
  summaryType: 'custom',
  displayFormat: '{0}',
  customizeText: ({ value }) => formatDuration(value),
}))

const sumDurations = (options) => {
  if (options.summaryProcess === 'start') options.totalValue = 0
  if (options.summaryProcess === 'calculate' && !options.value.is_excluded) options.totalValue += options.value[options.name] || 0
}

const gridOptions = (columns, durationFields) => ({
  keyExpr: 'key',
  columns,
  showBorders: true,
  showColumnLines: true,
  allowColumnResizing: true,
  columnResizingMode: 'widget',
  wordWrapEnabled: true,
  width: '100%',
  height: '46vh',
  paging: { enabled: false },
  sorting: { mode: 'multiple' },
  repaintChangesOnly: true,
  editing: { mode: 'cell', allowUpdating: true },
  selection: { mode: 'multiple', showCheckBoxesMode: 'always', selectAllMode: 'allPages' },
  onSelectionChanged,
  summary: { totalItems: summaryItems(durationFields), calculateCustomSummary: sumDurations },
  onRowPrepared: (e) => {
    if (e.rowType === 'data' && e.data.is_excluded) e.rowElement.classList.add('text-grey-5')
  },
  onCellPrepared: (e) => {
    if (!e.column?.allowEditing || !e.column.dataField) return
    if (e.rowType === 'header') e.cellElement.style.backgroundColor = 'color-mix(in srgb, var(--q-primary) 12%, transparent)'
    if (e.rowType === 'data' && isReportFieldModified(e.data, e.column.dataField)) e.cellElement.classList.add('bg-orange-2')
  },
  onRowUpdating: (e) => {
    e.cancel = true
    changeRow(e.key, e.newData)
  },
})

const timeDurationFields = ['pause_minutes', 'gross_minutes', 'net_minutes', 'travel_minutes', 'night_minutes']
const timeGridOptions = computed(() => gridOptions(timeColumns.value, timeDurationFields))
const expenseGridOptions = computed(() => gridOptions(expenseColumns.value, []))
const serviceGridOptions = computed(() => gridOptions(serviceColumns.value, []))
const timeIncludedKeys = computed(() => includedKeys(timeRows.value))
const expenseIncludedKeys = computed(() => includedKeys(expenseRows.value))
const serviceIncludedKeys = computed(() => includedKeys(serviceRows.value))

const serviceFields = computed(() => [
  { field: 'quantity', label: i18n.t('print.report.columns.quantity'), type: invoiceLineFieldTypeEnum.NUMBER },
  { field: 'staff_count', label: i18n.t('common.report.people'), type: invoiceLineFieldTypeEnum.NUMBER },
  { field: 'start', label: i18n.t('print.report.columns.start'), type: invoiceLineFieldTypeEnum.TEXT },
  { field: 'end', label: i18n.t('print.report.columns.end'), type: invoiceLineFieldTypeEnum.TEXT },
  { field: 'client_reference', label: i18n.t('common.requestReference'), type: invoiceLineFieldTypeEnum.TEXT },
])
const expenseFields = computed(() => [
  { field: 'notes', label: i18n.t('print.report.columns.notes'), type: invoiceLineFieldTypeEnum.TEXT },
  { field: 'quantity', label: i18n.t('print.report.columns.quantity'), type: invoiceLineFieldTypeEnum.NUMBER },
  { field: 'amount', label: i18n.t('print.report.columns.amount'), type: invoiceLineFieldTypeEnum.NUMBER },
  ...(showInternal.value ? [{ field: 'client_value', label: i18n.t('print.report.columns.client_value'), type: invoiceLineFieldTypeEnum.NUMBER }] : []),
])
const mobileFields = computed(() => [
  { field: 'gross_hours', label: i18n.t('print.report.columns.gross'), type: invoiceLineFieldTypeEnum.NUMBER },
  { field: 'pause_hours', label: i18n.t('print.report.columns.pause'), type: invoiceLineFieldTypeEnum.NUMBER },
  { field: 'travel_hours', label: i18n.t('print.report.columns.travel'), type: invoiceLineFieldTypeEnum.NUMBER },
  { field: 'night_hours', label: i18n.t('print.report.columns.night'), type: invoiceLineFieldTypeEnum.NUMBER },
  ...(showInternal.value ? [
    { field: 'client_hours', label: i18n.t('print.report.columns.client_hours'), type: invoiceLineFieldTypeEnum.NUMBER },
    { field: 'hourly_cost', label: i18n.t('print.report.columns.hourly_cost'), type: invoiceLineFieldTypeEnum.NUMBER },
    { field: 'client_value', label: i18n.t('print.report.columns.client_value'), type: invoiceLineFieldTypeEnum.NUMBER },
  ] : []),
  { field: 'comments', label: i18n.t('print.report.columns.comments'), type: invoiceLineFieldTypeEnum.TEXT },
])
const HOUR_FIELDS = Object.freeze({ gross_hours: 'gross_minutes', pause_hours: 'pause_minutes', travel_hours: 'travel_minutes', night_hours: 'night_minutes', client_hours: 'client_minutes' })
const toHours = (minutes) => (minutes == null ? null : Math.round(minutes / 60 * 100) / 100)
const openLine = (row) => {
  editedLine.value = {
    title: `${formatShortDate(row.date)} · ${row.project_name || ''}`,
    row: { ...row, ...Object.fromEntries(Object.entries(HOUR_FIELDS).map(([hours, minutes]) => [hours, toHours(row[minutes])])) },
    fields: { [reportRowKindEnum.EXPENSE]: expenseFields.value, [reportRowKindEnum.SERVICE]: serviceFields.value }[row.kind] || mobileFields.value,
    key: row.key,
  }
}
const applyLine = (newData, field, value) => {
  if (HOUR_FIELDS[field]) newData[HOUR_FIELDS[field]] = Math.round(Number(value) * 60)
  newData[field] = value
}
const onLineEdited = (changes) => {
  const minuteChanges = Object.fromEntries(Object.entries(changes).filter(([field]) => !HOUR_FIELDS[field]))
  changeRow(editedLine.value.key, minuteChanges)
  editedLine.value = null
}
</script>

<template>
  <div>
    <div class="row items-center justify-between q-mb-sm">
      <q-tabs
        v-model="tab"
        dense
        no-caps
        active-color="primary"
        indicator-color="primary"
        align="left"
      >
        <q-tab :name="reportRowKindEnum.TIME" :label="`${i18n.t('common.report.hours')} (${timeRows.length})`" />
        <q-tab :name="reportRowKindEnum.EXPENSE" :label="`${i18n.t('print.report.sections.expenses')} (${expenseRows.length})`" />
        <q-tab v-if="serviceRows.length" :name="reportRowKindEnum.SERVICE" :label="`${i18n.t('common.report.services')} (${serviceRows.length})`" />
      </q-tabs>
      <q-toggle v-model="showInternal" icon="mdi-lock-outline" :label="i18n.t('common.report.showInternalValues')" />
    </div>
    <q-tab-panels v-model="tab" animated>
      <q-tab-panel :name="reportRowKindEnum.TIME" class="q-pa-none">
        <q-list v-if="isMobile" dense>
          <q-item
            v-for="row in timeRows"
            :key="row.key"
            clickable
            class="q-pa-sm"
            :class="{ 'text-grey-5': row.is_excluded }"
            @click="openLine(row)"
          >
            <q-item-section avatar>
              <q-checkbox
                :model-value="!row.is_excluded"
                dense
                @click.stop
                @update:model-value="value => setExcluded(new Map([[row.key, !value]]))"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ formatShortDate(row.date) }} · {{ row.project_name }}<template v-if="!isStaffReport"> · {{ row.staff_label }}</template></q-item-label>
              <q-item-label caption>{{ formatTimeValue(row.start) }} – {{ formatTimeValue(row.end) }} · {{ i18n.t('print.report.columns.pause') }} {{ formatDuration(row.pause_minutes) }}</q-item-label>
            </q-item-section>
            <q-item-section side>
              <q-item-label class="text-weight-bold">{{ formatDuration(row.net_minutes) }}</q-item-label>
              <q-item-label caption>{{ formatDuration(row.gross_minutes) }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
        <dx-data-grid
          v-else
          v-bind="timeGridOptions"
          :data-source="timeRows"
          :selected-row-keys="timeIncludedKeys"
        />
      </q-tab-panel>
      <q-tab-panel :name="reportRowKindEnum.EXPENSE" class="q-pa-none">
        <q-list v-if="isMobile" dense>
          <q-item
            v-for="row in expenseRows"
            :key="row.key"
            clickable
            class="q-pa-sm"
            :class="{ 'text-grey-5': row.is_excluded }"
            @click="openLine(row)"
          >
            <q-item-section avatar>
              <q-checkbox
                :model-value="!row.is_excluded"
                dense
                @click.stop
                @update:model-value="value => setExcluded(new Map([[row.key, !value]]))"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ formatShortDate(row.date) }} · {{ row.project_name }}</q-item-label>
              <q-item-label caption>{{ row.notes }}</q-item-label>
            </q-item-section>
            <q-item-section side>{{ money(row.amount, row.currency_code) }}</q-item-section>
          </q-item>
        </q-list>
        <dx-data-grid
          v-else
          v-bind="expenseGridOptions"
          :data-source="expenseRows"
          :selected-row-keys="expenseIncludedKeys"
        />
      </q-tab-panel>
      <q-tab-panel :name="reportRowKindEnum.SERVICE" class="q-pa-none">
        <q-list v-if="isMobile" dense>
          <q-item
            v-for="row in serviceRows"
            :key="row.key"
            clickable
            class="q-pa-sm"
            :class="{ 'text-grey-5': row.is_excluded }"
            @click="openLine(row)"
          >
            <q-item-section avatar>
              <q-checkbox
                :model-value="!row.is_excluded"
                dense
                @click.stop
                @update:model-value="value => setExcluded(new Map([[row.key, !value]]))"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ formatShortDate(row.date) }} · {{ row.project_name }}</q-item-label>
              <q-item-label caption>{{ row.comments }}<template v-if="row.start"> · {{ formatTimeValue(row.start) }} – {{ formatTimeValue(row.end) }}</template></q-item-label>
            </q-item-section>
            <q-item-section side>
              <q-item-label class="text-weight-bold">{{ +row.quantity }}</q-item-label>
              <q-item-label caption>{{ money(row.amount, row.currency_code) }}</q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
        <dx-data-grid
          v-else
          v-bind="serviceGridOptions"
          :data-source="serviceRows"
          :selected-row-keys="serviceIncludedKeys"
        />
      </q-tab-panel>
    </q-tab-panels>
    <info-box :id="infoBoxIdEnum.REPORT_ROWS_EDITABLE" class="q-mt-md" :message="i18n.t('common.report.rowsEditableInfo')" />
    <q-dialog :model-value="editedLine !== null" maximized @hide="editedLine = null">
      <invoice-line-edit
        v-if="editedLine !== null"
        :title="editedLine.title"
        :row="editedLine.row"
        :fields="editedLine.fields"
        :apply="applyLine"
        @update="onLineEdited"
        @cancel="editedLine = null"
      />
    </q-dialog>
  </div>
</template>
